import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, fmtBR, formatApiError } from "@/lib/api";

function StatCard({ label, value, testId }) {
  return (
    <div className="border border-border p-2" data-testid={testId}>
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="font-heading text-base font-bold">{value}</p>
    </div>
  );
}

export function PedagogicalReportDialog({ studentId, studentName, onClose }) {
  const [report, setReport] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async (params = {}) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/pedagogy/students/${studentId}/report`, { params });
      setReport(data);
    } catch (err) {
      toast.error(formatApiError(err));
      onClose();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  const applyPeriod = (e) => {
    e.preventDefault();
    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;
    load(params);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="report-dialog">
        <DialogHeader>
          <DialogTitle>Relatório pedagógico — {studentName}</DialogTitle>
          <DialogDescription>Frequência e progresso de objetivos são indicadores independentes.</DialogDescription>
        </DialogHeader>
        {loading || !report ? (
          <p className="text-sm text-muted-foreground">Carregando...</p>
        ) : (
          <div className="space-y-5">
            <form onSubmit={applyPeriod} className="flex flex-wrap items-end gap-2">
              <div className="space-y-1">
                <Label className="text-xs">De</Label>
                <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="report-from-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Até</Label>
                <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="report-to-input" />
              </div>
              <Button type="submit" size="sm" variant="outline" data-testid="report-apply-button">Filtrar período</Button>
              {(report.period.from || report.period.to) && (
                <span className="text-xs text-muted-foreground" data-testid="report-period-label">
                  Período: {report.period.from ? fmtBR(report.period.from) : "início"} — {report.period.to ? fmtBR(report.period.to) : "hoje"}
                </span>
              )}
            </form>

            <div className="space-y-1" data-testid="report-enrollments">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Matrículas</p>
              {report.enrollments.map((e) => (
                <p key={e.id} className="text-sm" data-testid={`report-enrollment-${e.id}`}>
                  {e.course_name} · {e.teacher_name} · <span className="text-muted-foreground">{e.status}</span>
                </p>
              ))}
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Frequência</p>
              <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="report-frequency">
                <StatCard label="Registradas" value={report.frequency.registradas} testId="report-freq-registered" />
                <StatCard label="Presenças" value={report.frequency.presencas} testId="report-freq-present" />
                <StatCard label="Faltas" value={report.frequency.faltas} testId="report-freq-absent" />
                <StatCard label="Frequência" value={`${report.frequency.percentual}%`} testId="report-freq-percent" />
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Objetivos pedagógicos</p>
              <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="report-objectives">
                <StatCard label="Objetivos" value={report.objectives.total} testId="report-obj-total" />
                <StatCard label="Concluídos" value={report.objectives.concluidos} testId="report-obj-done" />
                <StatCard label="Em andamento" value={report.objectives.em_andamento} testId="report-obj-progress" />
                <StatCard label="Progresso" value={`${report.objectives.percentual}%`} testId="report-obj-percent" />
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Últimas aulas com registro</p>
              <div className="mt-2 space-y-2" data-testid="report-recent-lessons">
                {report.recent_lessons.length === 0 && (
                  <p className="text-sm text-muted-foreground" data-testid="report-lessons-empty">
                    Nenhuma aula com registro no período.
                  </p>
                )}
                {report.recent_lessons.map((l) => (
                  <div key={l.id} className="border border-border p-2" data-testid={`report-lesson-${l.id}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-medium">
                        {fmtBR(l.date)} · {l.start_time} · {l.course_name} · {l.teacher_name}
                      </p>
                      <Badge
                        variant={l.attendance === "falta" ? "destructive" : "default"}
                        className={l.attendance === "presente" ? "bg-primary text-primary-foreground" : ""}
                      >
                        {l.attendance === "presente" ? "Presente" : "Falta"}
                      </Badge>
                    </div>
                    {l.diary_content && <p className="mt-1 text-xs text-muted-foreground">Conteúdo: {l.diary_content}</p>}
                    {l.diary_notes && <p className="mt-0.5 text-xs text-muted-foreground">Observação: {l.diary_notes}</p>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
